import React, { useContext, useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { Alert, Button } from '@mui/material'
import { Snackbar } from '@mui/material'
import * as Yup from 'yup'
import { useFormik } from 'formik'

import { CustomInput, CustomSelect, CustomTextarea, CustomUplaodInput } from '../../../components/FormComponets'
import ImagePreview from '../../../components/ImagePreview'
import Modal from '../../../components/Modal'
import { serviceRequestApi } from '../../../api/serviceRequest'
import LoadingOverlay from '../../../components/LoadingOverlay'
import { ContractEntity, ServiceRequestCategoryEntity, ServiceRequestUrgencyEntity } from '../../../entities'
import { GlobalContext } from '../../../App'
import { ContextEntity } from '../../../customEntites/context.entity'
import { AttachmentEntity } from '../../../customEntites/Attachment.entity'
import { generateError } from '../../../services/error.service'
import { ErrorSummary } from '../../../components/ErrorSummary'
import { getData, removeData, saveData } from '../../../services/dataPreserve.service'

export const RepairAndInquiriesRequest = () => {
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const { context }: ContextEntity = useContext(GlobalContext)

  const [attachments, setAttachments] = useState<AttachmentEntity[]>([])
  const [submitted, setSubmitted] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(false)
  const [categoriesLoading, setCategoriesLoading] = useState(false)
  const [urgenciesLoading, setUrgenciesLoading] = useState(false)
  const [categoryOptions, setCategoryOptions] = useState<{label: string, value: string}[]>([])// DEPRECATED BY DOKUWEB - Categories no longer needed once dokuweb is fully tested
  const [urgencyOptions, setUrgencyOptions] = useState<{label: string, value: string}[]>([])
  const [contractNumberOptions, setContractNumberOptions] = useState<{label: string, value: string}[]>([])
  const [showErrorAlert, setShowErrorAlert] = useState(false)
  const [errorAlertMsg, setErrorAlertMsg] = useState('')
  // DEPRECATED BY DOKUWEB - Feature flag check no longer needed once dokuweb is fully tested
  const isDokuwebEnabled = process.env.REACT_APP_DOKUWEB_ENABLED === "true"

  const inputRefs = {
    subject: useRef<HTMLInputElement>(null),
    contractNumber: useRef<any>(null),
    category: useRef<any>(null),// DEPRECATED BY DOKUWEB - Can remove category field once dokuweb is fully tested
    description: useRef<HTMLTextAreaElement>(null),
    urgency: useRef<any>(null)
  }

  const SignupSchema = Yup.object().shape({
    subject: Yup.string().required(`${t('FORM.SUBJECT_REQUIRED')}`),
    contractNumber: Yup.string().required(`${t('FORM.CONTRACT_NUMBER_REQUIRED')}`),
    category: isDokuwebEnabled ? Yup.string().optional() : Yup.string().required(`${t('FORM.CATEGORY_REQUIRED')}`),// DEPRECATED BY DOKUWEB - Category validation made optional when dokuweb enabled, can remove category field once dokuweb is fully tested
    description: Yup.string().required(`${t('FORM.DESCRIPTION_REQUIRED')}`),
    urgency: Yup.string().required(`${t('FORM.URGENCY_REQUIRED')}`)
  })

  const formik = useFormik({
    initialValues: {
      subject: '',
      contractNumber: '',
      category: '',// DEPRECATED BY DOKUWEB - Can remove category field once dokuweb is fully tested
      description: '',
      urgency: ''
    },
    onSubmit: values => {
      setShowLoadingOverlay(true);
      (async () => {
        const categoryObject: ServiceRequestCategoryEntity = isDokuwebEnabled ? {} : JSON.parse(formik.values.category)// DEPRECATED BY DOKUWEB - Category validation made optional when dokuweb enabled, can remove category field once dokuweb is fully tested
        const urgencyObject: ServiceRequestCategoryEntity = JSON.parse(formik.values.urgency)
        const contract: ContractEntity|undefined = context?.contracts?.activeContracts?.find((contract) => contract?.contractNumber === formik.values.contractNumber)
        if (contract) {
          const attachmentsFiles = attachments.map((attachment) => attachment.file || attachment)
          await serviceRequestApi.createServiceRequest(
            {
              ...values,
              category: isDokuwebEnabled ? 'App Tickets' : categoryObject.id,// DEPRECATED BY DOKUWEB - Category validation made optional when dokuweb enabled, can remove category field once dokuweb is fully tested
              urgency: urgencyObject.name,
              contractNumber: formik.values.contractNumber,
              phone: contract?.phone,
              email: contract?.email,
              name: contract?.name,
              contact_id: contract?.contactId,
              contract_id: contract?.contractId
            },
            attachmentsFiles
          )
            .then(() => {
              setShowSuccessModal(true)
              setShowErrorAlert(false)
            })
            .catch((err) => {
              if (err?.status === 401) {
                saveData('service_request', {
                  subject: formik.values.subject,
                  contractNumber: formik.values.contractNumber,
                  category: formik.values.category,// DEPRECATED BY DOKUWEB - Can remove category field once dokuweb is fully tested
                  description: formik.values.description,
                  urgency: formik.values.urgency
                })
              }
              setShowErrorAlert(true)
              setErrorAlertMsg(generateError(err?.response?.data?.message))
              window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
            })
        }
        setShowLoadingOverlay(false)
      })()
    },
    validationSchema: SignupSchema
  })

  useEffect(() => {
    (async () => {
      if (!isDokuwebEnabled) await setCategories()
      await setUrgencies()
    })()
  }, [t])

  useEffect(() => {
    if (context?.contracts?.activeContracts) {
      const contracts = context?.contracts?.activeContracts
      const contractNumberOptions = []
      for (const contract of contracts) {
        if (contract?.contractNumber) {
          contractNumberOptions.push({ value: contract?.contractNumber, label: `${contract?.contractNumber?.replace('-', '/')}, ${contract?.address}` })
        }
      }
      setContractNumberOptions(contractNumberOptions)
    }
  }, [context?.contracts?.activeContracts])

  useEffect(() => {
    if (formik.isSubmitting) {
      setSubmitted(true)
    }
  }, [formik])

  useEffect(() => {
    const data = getData('service_request')
    if (data) {
      formik.setValues({
        subject: data.subject,
        contractNumber: data.contractNumber,
        category: data.category,// DEPRECATED BY DOKUWEB - Can remove category field once dokuweb is fully tested
        description: data.description,
        urgency: data.urgency,
      })
      removeData('service_request')
    }
  }, [])
// DEPRECATED BY DOKUWEB - Categories no longer needed once dokuweb is fully tested
  const setCategories = async () => {
    setCategoriesLoading(true)
    const lang = i18n.language.toUpperCase() || 'DE'
    await serviceRequestApi.getCategories(lang)
      .then((result) => {
        const categoryOptions = []
        for (const category of result) {
          if (category.name && category.id) {
            categoryOptions.push({
              value: JSON.stringify(category),
              label: category.name
            })
          }
        }
        setCategoryOptions(categoryOptions)
        setShowErrorAlert(false)
      })
      .catch((err) => {
        setShowErrorAlert(true)
        setErrorAlertMsg(generateError(err?.response?.data?.message))
      })
    setCategoriesLoading(false)
  }

  const setUrgencies = async () => {
    setUrgenciesLoading(true)
    await serviceRequestApi.getUrgencies()
      .then((result) => {
        const urgencyOptions = []
        for (const urgency of result) {
          if (urgency.name && urgency.id) {
            const localizedString = urgency.name.split(' ').join('').toUpperCase()
            urgencyOptions.push({
              value: JSON.stringify(urgency),
              label:t(`REPAIR_AND_INQUIRIES.URGENCY_OPTIONS.${localizedString}` as keyof typeof t)
            })
          }
        }
        setUrgencyOptions(urgencyOptions)
        setShowErrorAlert(false)
      })
      .catch((err) => {
        setShowErrorAlert(true)
        setErrorAlertMsg(generateError(err?.response?.data?.message))
      })
    setUrgenciesLoading(false)
  }

  const onSuccessModalCancel = () => {
    setShowSuccessModal(false)
    navigate('/repairs-and-inquiries', { state: { from: '/repairs-and-inquiries/new-request' }})
  }

  return (
    <div>
      <ErrorSummary
        errors={formik.errors}
        submitted={submitted}
        fieldRefs={inputRefs}
        errorMessageOrder={['contractNumber', 'subject', 'category', 'urgency', 'description']}
      />
      {showLoadingOverlay && (
        <LoadingOverlay />
      )}
      {showSuccessModal && (
        <Modal
          head={`${t('REPAIR_AND_INQUIRIES.THANK_YOU')}`}
          text={`${t('REPAIR_AND_INQUIRIES.SUCCESSFULLY_REQUEST')}`}
          cancelText={`${t('REPAIR_AND_INQUIRIES.BACK_TO_REPAIRS_AND_INQUIRES')}`}
          onCancel={() => onSuccessModalCancel()}
          onConfirm={null}
          confirmText={null}
        />
      )}
      <form className='form-center' onSubmit={formik.handleSubmit} aria-labelledby='repair-and-inquiries-request-form'>
        <CustomSelect
          inputRef={inputRefs.contractNumber}
          label={`${t('REPAIR_AND_INQUIRIES.CONTRACT_NUMBER')} *`}
          name="contractNumber"
          type="text"
          onChange={formik.setFieldValue}
          value={{
            value: formik.values.contractNumber,
            label: (() => {
              const contract = context?.contracts?.activeContracts?.find((contract) => contract?.contractNumber === formik.values.contractNumber)
              const contractNumberObject: any = contract ? contract : ''
              return typeof contractNumberObject === 'object' ? (
                `${contractNumberObject.contractNumber?.replace('-', '/')}, ${contractNumberObject.address}`
              ) : contractNumberObject
            })()
          }}
          placeholder={`${t('REPAIR_AND_INQUIRIES.SELECT_CONTRACT_NUMBER')} *`}
          options={contractNumberOptions}
          error={submitted ? formik.errors.contractNumber : undefined}
        />
        <CustomInput
          inputRef={inputRefs.subject}
          label={`${t('REPAIR_AND_INQUIRIES.SUBJECT')} *`}
          name="subject"
          type="text"
          onChange={formik.handleChange}
          value={formik.values.subject}
          placeholder={`${t('REPAIR_AND_INQUIRIES.ENTER_SUBJECT')} *`}
          error={submitted ? formik.errors.subject : undefined}
          autoComplete='off'
        />
        {/* DEPRECATED BY DOKUWEB - Categories no longer needed once dokuweb is fully tested */}
        {!isDokuwebEnabled && <CustomSelect
          inputRef={inputRefs.category}
          label={`${t('REPAIR_AND_INQUIRIES.CATEGORY')} *`}
          name="category"
          type="text"
          onChange={formik.setFieldValue}
          loading={categoriesLoading}
          value={{
            value: formik.values.category,
            label: ((): string => {
              const categoryObject: ServiceRequestCategoryEntity = formik.values.category ? JSON.parse(formik.values.category) : ''
              return typeof categoryObject === 'object' ? (
                categoryObject.name
              ) : categoryObject
            })()
          }}
          placeholder={`${t('REPAIR_AND_INQUIRIES.SELECT_CATEGORY')} *`}
          options={categoryOptions}
          error={submitted ? formik.errors.category : undefined}
        />}
        <CustomSelect
          inputRef={inputRefs.urgency}
          label={`${t('REPAIR_AND_INQUIRIES.URGENCY')} *`}
          name="urgency"
          type="text"
          onChange={formik.setFieldValue}
          loading={urgenciesLoading}
          value={{
            value: formik.values.urgency,
            label: (() => {
              const urgencyObject: ServiceRequestUrgencyEntity = formik.values.urgency ? JSON.parse(formik.values.urgency) : ''
              return typeof urgencyObject === 'object' ? (
                t(`REPAIR_AND_INQUIRIES.URGENCY_OPTIONS.${urgencyObject.name.split(' ').join('').toUpperCase()}`)
              ) : urgencyObject
            })()
          }}
          placeholder={`${t('REPAIR_AND_INQUIRIES.SELECT_URGENCY')} *`}
          options={urgencyOptions}
          error={submitted ? formik.errors.urgency : undefined}
        />
        <CustomTextarea
          inputRef={inputRefs.description}
          label={`${t('REPAIR_AND_INQUIRIES.DESCRIPTION')} *`}
          name="description"
          onChange={formik.handleChange}
          value={formik.values.description}
          placeholder={`${t('REPAIR_AND_INQUIRIES.ENTER_DESCRIPTION')} *`}
          error={submitted ? formik.errors.description : undefined}
        />
        <ImagePreview
          attachments={attachments}
          setAttachments={setAttachments}
          uploader={
            <CustomUplaodInput
              supportedFileTypes='(pdf / jpg / jpeg / png)'
              setAttachments={setAttachments}
              label={`${t('REPAIR_AND_INQUIRIES.UPLOAD_ATTACHMENT')}`}
            />
          }
          remove={true}
        />
        <div className='buttons-holder center'>
          <Button type='submit' className='main-btn' variant='contained' size='large' aria-label={String(t('BUTTON.SUBMIT'))}>{t('BUTTON.SUBMIT')}</Button>
          <Button onClick={() => navigate('/repairs-and-inquiries')} className='secondary-btn' variant='contained' size='large' aria-label={String(t('BUTTON.CANCEL'))}>{t('BUTTON.CANCEL')}</Button>
        </div>
      </form>
      <Snackbar open={showErrorAlert} autoHideDuration={6000} onClose={() => setShowErrorAlert(false)} anchorOrigin={{horizontal:'right', vertical:'bottom'}}>
        <Alert severity='error' sx={{ width: '100%' }} variant='filled' role='alert' aria-live='assertive'>
          {errorAlertMsg}
        </Alert>
      </Snackbar>
    </div>
  )
}
